import { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'wouter';
import { BottomTab } from './bottom-tab';
import { OrdersTabItem } from './bottom-tab/orders-tab-item';
import { TAB_ITEMS } from '@/shared/constants/tab-items';
import { useBottomTab } from '@/shared/contexts/bottom-tab-context';
import { useHomeNavigation } from '@/shared/hooks/use-home-navigation';
import { useClubTab } from '@/modules/club/hooks/use-club-tab';
import { ClubTabItem } from '@/modules/club/components/club-tab-item';
import { BOTTOM_ACTION_SLOT_ID } from './bottom-action-bar';
import { ProfileTabItem } from '@/modules/profile-completion/components/profile-tab-item';

const CONTAINER_ID = 'persistent-bottom-tab';
const CONTAINER_CLASS =
  'fixed bottom-0 left-0 right-0 z-50 bg-white pb-[env(safe-area-inset-bottom)]';

/** Contêiner fixo no `body`, um por instância do rodapé — criado uma única vez. */
function createContainer(): HTMLDivElement {
  const container = document.createElement('div');
  container.id = CONTAINER_ID;
  container.className = CONTAINER_CLASS;
  return container;
}

export function PersistentBottomTab() {
  const [location, navigate] = useLocation();
  const { handleGoToHome } = useHomeNavigation();
  const { hideBottomTab, hideBottomTabShadow, setActionSlot } = useBottomTab();
  // Clube pratiGO (PRAT-27): a aba só existe quando a loja em uso tem o programa ativo.
  const clubTab = useClubTab();

  // O nó do contêiner nasce com o componente e nunca é trocado: o portal fica
  // sempre apontado para o mesmo elemento. Antes ele era criado num efeito e
  // destruído no cleanup; quando o React desliga e religa os efeitos sem
  // desmontar (ex.: a rota lazy da revisão do pedido suspende o Suspense e
  // depois reaparece), o portal ficava apontado para um nó já removido do
  // `body` e as abas (e o slot de ação dentro delas) sumiam até recarregar.
  const [container] = useState(createContainer);

  // Entra no `body` antes de pintar; sai quando o rodapé desmonta. Se o
  // efeito religar, o MESMO nó volta, com o conteúdo do portal intacto.
  useLayoutEffect(() => {
    if (!container.isConnected) document.body.appendChild(container);
    return () => {
      container.remove();
    };
  }, [container]);

  // Update visibility based on context state
  useEffect(() => {
    container.style.display = hideBottomTab ? 'none' : 'block';
  }, [container, hideBottomTab]);

  if (hideBottomTab) {
    return null;
  }

  return createPortal(
    <>
      {/* Barra de ação da tela (ex.: Ver sacola) entra aqui, logo acima das abas
          (PRAT-154). O nó é registrado no contexto: o BottomActionBar renderiza
          por portal assim que ele existe e volta ao modo solo quando sai. */}
      <div id={BOTTOM_ACTION_SLOT_ID} ref={setActionSlot} />
      <BottomTab.Root hideShadow={hideBottomTabShadow} dense={clubTab.isVisible}>
        {TAB_ITEMS.map(item => {
          // Usar o componente especial para o item de pedidos
          if (item.path === '/orders') {
            return (
              <OrdersTabItem
                key={item.path}
                icon={item.icon}
                title={item.title}
                onClick={() => navigate(item.path)}
              />
            );
          }

          // Clube pratiGO entra entre Pedidos e Perfil (ordem da barra: Início,
          // Busca, Pedidos, Clube pratiGO, Perfil).
          if (item.path === '/profile' && clubTab.isVisible) {
            return [
              <ClubTabItem
                key="/clube"
                isActive={location.includes('/clube')}
                onClick={() => navigate(`/${clubTab.slug}/clube`)}
              />,
              <ProfileTabItem
                key={item.path}
                icon={item.icon}
                title={item.title}
                onClick={() => navigate(item.path)}
              />,
            ];
          }

          // Perfil com ponto quando falta e-mail/CPF (PRAT-161).
          if (item.path === '/profile') {
            return (
              <ProfileTabItem
                key={item.path}
                icon={item.icon}
                title={item.title}
                onClick={() => navigate(item.path)}
              />
            );
          }

          // Para o item Home, usar a navegação especial
          if (item.path === '/') {
            return (
              <BottomTab.Item
                key={item.path}
                icon={item.icon}
                title={item.title}
                onClick={handleGoToHome}
              />
            );
          }

          // Usar o componente padrão para os outros itens
          return (
            <BottomTab.Item
              key={item.path}
              icon={item.icon}
              title={item.title}
              onClick={() => navigate(item.path)}
            />
          );
        })}
      </BottomTab.Root>
    </>,
    container
  );
}
